import React from 'react';
import Icon from '../components/Icon';
import { EditableText } from '../components/Editable';
import { PRODUCT_FIELDS, formatFieldValue, gapLabel, isEmptyValue, parseFieldValue } from '../product/productUtils';
import { questionsForGaps } from './onboardingUtils';

const SECTIONS = [...new Set(PRODUCT_FIELDS.map(field => field.section))];

const FieldValue = ({ id, field, onSave }) => {
    const text = formatFieldValue(id, field.value);
    const display = id === 'features' && !isEmptyValue(field.value) ? (
        <span className="ls-chips">
            {field.value.map(feature => <span key={feature.name} className="ls-chip is-medium" style={{ padding: '3px 9px' }}>{feature.name}</span>)}
        </span>
    ) : undefined;
    return (
        <span style={{ minWidth: 0 }}>
            <EditableText
                value={text}
                display={display}
                multiline={id === 'oneLiner' || id === 'targetCustomer'}
                label={PRODUCT_FIELDS.find(f => f.id === id).label}
                placeholder="Not found — click to add"
                onSave={(value) => onSave(parseFieldValue(id, value, field.value))}
                style={{ fontWeight: id === 'name' ? 600 : undefined }}
            />
            {field.confidence === 'low' && !field.edited && !isEmptyValue(field.value) && <span className="ls-low-flag">Low confidence</span>}
        </span>
    );
};

const ReviewStep = ({ draft, setDraft, isBusy, onContinue, onAddSource }) => {
    const { fields, sources, sourceCounts } = draft;
    const filled = PRODUCT_FIELDS.filter(field => !isEmptyValue(fields[field.id]?.value)).length;
    const questions = questionsForGaps(fields, draft.questions);
    const readySources = sources.filter(source => source.status === 'ready');
    const minutes = Math.max(1, Math.round(questions.length * 0.5));

    const saveField = (id, value) => setDraft(prev => ({
        ...prev,
        fields: { ...prev.fields, [id]: { value, source: 'You', confidence: 'high', edited: true } },
    }));

    return (
        <>
            <div className="ls-flow-split">
                <aside className="ls-flow-aside">
                    <div>
                        <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-.02em' }}>
                            {filled} <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--muted)' }}>of {PRODUCT_FIELDS.length} fields</span>
                        </div>
                        <div style={{ fontSize: 14, color: 'var(--secondary)' }}>
                            {readySources.length ? 'filled from your sources' : 'filled in so far'}
                        </div>
                    </div>
                    {readySources.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14 }}>
                            {readySources.map(source => (
                                <div key={source.id} style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
                                    <Icon name="check_circle" size={18} color="var(--accent-text)" />
                                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{source.title}</span>
                                    <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--muted)' }}>{sourceCounts[source.id] ?? 0}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <div style={{ height: 1, background: 'var(--subtle)' }} />
                    <div style={{ fontSize: 13, color: 'var(--secondary)', lineHeight: 1.5 }}>
                        Click any value to edit. Changes here become the facts the AI writes from.
                    </div>
                    <button type="button" className="ls-link" style={{ fontSize: 14 }} onClick={onAddSource}>+ Add another source</button>
                </aside>

                <div className="ls-flow-main">
                    {SECTIONS.map(section => (
                        <div className="ls-review-card" key={section}>
                            <div className="ls-review-card-title">{section}</div>
                            {PRODUCT_FIELDS.filter(field => field.section === section).map(({ id, label }) => {
                                const field = fields[id] || { value: null, source: '', confidence: 'low' };
                                const isMissing = isEmptyValue(field.value);
                                const isLow = !isMissing && field.confidence === 'low' && !field.edited;
                                return (
                                    <div key={id} className={`ls-review-row${isLow ? ' is-low' : ''}${isMissing ? ' is-missing' : ''}`}>
                                        <span style={{ color: 'var(--muted)' }}>{label}</span>
                                        <FieldValue id={id} field={field} onSave={(value) => saveField(id, value)} />
                                        <span className="ls-review-source" title={field.source}>{isMissing ? '' : field.source}</span>
                                    </div>
                                );
                            })}
                        </div>
                    ))}

                    {questions.length > 0 && (
                        <div className="ls-review-card" style={{ borderColor: 'var(--warn-border)', boxShadow: 'none' }}>
                            <div className="ls-review-card-title" style={{ borderBottomColor: '#fdecc8' }}>
                                Not found in your sources
                                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--warn-text)' }}>We'll ask about these</span>
                            </div>
                            <div className="ls-chips" style={{ gap: 8, padding: '14px 20px' }}>
                                {questions.map(q => <span key={q.field} className="ls-gap-chip">{gapLabel(q.field, fields[q.field]?.value)}</span>)}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <footer className="ls-flow-footer">
                {questions.length > 0 && (
                    <span style={{ fontSize: 14, color: 'var(--secondary)' }}>
                        {questions.length} question{questions.length === 1 ? '' : 's'} · about {minutes} minute{minutes === 1 ? '' : 's'}
                    </span>
                )}
                <button type="button" className="ls-btn ls-btn-primary" style={{ fontSize: 15, padding: '12px 24px' }} onClick={onContinue} disabled={isBusy}>
                    {isBusy ? 'Saving…' : questions.length ? 'Answer questions' : 'Draft ICPs'}
                </button>
            </footer>
        </>
    );
};

export default ReviewStep;
